import { Controller } from "@hotwired/stimulus";

// Slides are 960×540 boards. The canvas holding them is drawn at that size
// and scaled as one piece to fit the stage, so a slide looks the same in a
// meeting room as on a laptop instead of reflowing per screen.
//
// Three ways to look at a deck:
//   - one board at a time (the default), scaled to the stage;
//   - the overview (O), every board small in a grid, click one to go there;
//   - stacked, on a narrow screen: every board in a column at the width of
//     the screen, pinch to zoom, scroll to move — a 960px board scaled to a
//     phone is 6px type, so the phone reads the deck as a page instead.
const BOARD_WIDTH = 960;
const BOARD_HEIGHT = 540;
const STACKED_BELOW = 640;
const SWIPE_MIN = 60;

export default class extends Controller {
  static targets = [
    "slide",
    "stage",
    "canvas",
    "counter",
    "progress",
    "chapter",
    "overviewButton",
    "prevButton",
    "nextButton",
    "hide",
    "notesPanel",
    "notesButton",
    "notes",
    "nextUp",
    "clock",
    "shareUrl",
    "theme",
  ];
  static values = {
    index: { type: Number, default: 0 },
    channel: { type: String, default: "" },
    presenter: { type: Boolean, default: false },
  };

  connect() {
    this.totalSlides = this.slideTargets.length;
    this.slugByIndex = this.slideTargets.map(
      (slide) => slide.dataset.slideSlug || null,
    );
    this.indexBySlug = new Map();
    this.slugByIndex.forEach((slug, i) => {
      if (slug && !this.indexBySlug.has(slug)) this.indexBySlug.set(slug, i);
    });

    this.stacked = this.shouldStack();
    this.overview = false;
    this.notesOpen = this.presenterValue;
    this.fit();

    // The presenter window and the room's window share a channel: whichever
    // turns the page, both move.
    if (this.channelValue && "BroadcastChannel" in window) {
      this.channel = new BroadcastChannel(this.channelValue);
      this.channel.onmessage = (event) => {
        if (event.data?.index !== undefined && event.data.index !== this.indexValue) {
          this.showSlide(event.data.index, true, false);
        }
      };
    }
    if (this.presenterValue) {
      this.startClock();
      this.element.classList.add("deck--presenter");
    }

    // Read initial slide from URL hash — accepts either a semantic slug
    // (#revenue, #forecast, …) or the legacy positional form (#slide-3).
    let initial = this.slideFromHash();
    if (this.hiddenAt(initial)) initial = this.visibleIndices()[0] ?? 0;
    this.showSlide(initial, false, false);

    this.boundKeydown = this.handleKeydown.bind(this);
    this.boundHashChange = this.handleHashChange.bind(this);
    this.boundPointerDown = this.handlePointerDown.bind(this);
    this.boundPointerUp = this.handlePointerUp.bind(this);
    this.boundPick = this.handlePick.bind(this);
    document.addEventListener("keydown", this.boundKeydown);
    window.addEventListener("hashchange", this.boundHashChange);

    if (this.hasStageTarget) {
      this.stageTarget.addEventListener("pointerdown", this.boundPointerDown);
      this.stageTarget.addEventListener("pointerup", this.boundPointerUp);
      if ("ResizeObserver" in window) {
        this.resizeObserver = new ResizeObserver(() => this.fit());
        this.resizeObserver.observe(this.stageTarget);
      }
    }
    if (this.hasCanvasTarget) {
      this.canvasTarget.addEventListener("click", this.boundPick);
    }
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundKeydown);
    window.removeEventListener("hashchange", this.boundHashChange);
    if (this.hasStageTarget) {
      this.stageTarget.removeEventListener(
        "pointerdown",
        this.boundPointerDown,
      );
      this.stageTarget.removeEventListener("pointerup", this.boundPointerUp);
    }
    if (this.hasCanvasTarget) {
      this.canvasTarget.removeEventListener("click", this.boundPick);
    }
    this.resizeObserver?.disconnect();
    this.channel?.close();
    clearInterval(this.clockTimer);
  }

  // Boards hidden for this presentation are skipped when paging; the
  // overview still shows them, dimmed, with the button to bring them back.
  hiddenAt(index) {
    return this.slideTargets[index]?.dataset.hidden === "true";
  }

  visibleIndices() {
    return this.slideTargets.map((_, i) => i).filter((i) => !this.hiddenAt(i));
  }

  // --- layout ---------------------------------------------------------

  shouldStack() {
    return window.matchMedia(`(max-width: ${STACKED_BELOW}px)`).matches;
  }

  // Largest scale at which the whole board fits, with a gutter of stage
  // colour around it. On a narrow screen the boards stack instead, each at
  // the width of the screen.
  fit() {
    if (!this.hasStageTarget || !this.hasCanvasTarget) return;

    const stacked = this.shouldStack();
    if (stacked !== this.stacked) {
      this.stacked = stacked;
      this.showSlide(this.indexValue, false);
    }
    this.stageTarget.classList.toggle("deck-stage--stacked", stacked);

    const gutter = this.stageTarget.clientWidth < 640 ? 16 : 32;
    // The presenter window keeps the board to its left column.
    const room = this.presenterValue ? this.stageTarget.clientWidth * 0.62 : this.stageTarget.clientWidth;
    const width = room - gutter * 2;
    const height = this.stageTarget.clientHeight - gutter * 2;
    const scale = Math.max(
      Math.min(width / BOARD_WIDTH, height / BOARD_HEIGHT),
      0.1,
    );

    this.canvasTarget.style.setProperty("--deck-scale", scale.toFixed(4));
    this.canvasTarget.style.setProperty(
      "--deck-stack-zoom",
      Math.max((this.stageTarget.clientWidth - 24) / BOARD_WIDTH, 0.1).toFixed(
        4,
      ),
    );
    if (stacked) this.hydrateAll();
  }

  // --- keyboard -------------------------------------------------------

  handleKeydown(event) {
    if (event.metaKey || event.ctrlKey || event.altKey) return;

    switch (event.key) {
      case "ArrowRight":
      case "PageDown":
      case " ":
        event.preventDefault();
        this.next();
        break;
      case "ArrowLeft":
      case "PageUp":
        event.preventDefault();
        this.previous();
        break;
      case "Home":
        event.preventDefault();
        this.showSlide(0);
        break;
      case "End":
        event.preventDefault();
        this.showSlide(this.totalSlides - 1);
        break;
      case "o":
      case "O":
        event.preventDefault();
        this.toggleOverview();
        break;
      case "f":
      case "F":
        event.preventDefault();
        this.toggleFullscreen();
        break;
      case "n":
      case "N":
        event.preventDefault();
        this.toggleNotes();
        break;
      case "Escape": {
        event.preventDefault();
        // Close what is open before leaving: a menu, the overview, then
        // fullscreen (which the browser closes itself), then the deck.
        const openMenu = this.element.querySelector(
          '[data-dropdown-target="menu"]:not(.hidden)',
        );
        if (openMenu) {
          openMenu.classList.add("hidden");
        } else if (this.overview) {
          this.toggleOverview();
        } else if (!document.fullscreenElement) {
          this.exit();
        }
        break;
      }
    }
  }

  // --- touch ----------------------------------------------------------

  // A horizontal swipe on the stage turns the page. Only for touch: a mouse
  // drag is a selection, and the edge zones already take a click.
  handlePointerDown(event) {
    if (event.pointerType !== "touch" || this.overview || this.stacked) return;
    this.swipe = { x: event.clientX, y: event.clientY };
  }

  handlePointerUp(event) {
    if (!this.swipe || event.pointerType !== "touch") return;
    const dx = event.clientX - this.swipe.x;
    const dy = event.clientY - this.swipe.y;
    this.swipe = null;
    if (Math.abs(dx) < SWIPE_MIN || Math.abs(dy) > Math.abs(dx)) return;
    if (dx < 0) this.next();
    else this.previous();
  }

  // --- hash -----------------------------------------------------------

  handleHashChange() {
    const index = this.slideFromHash();
    if (index !== this.indexValue) {
      this.showSlide(index, false);
    }
  }

  slideFromHash() {
    const raw = window.location.hash.replace(/^#/, "");
    if (!raw) return 0;

    // Legacy positional form: #slide-3 → index 2.
    const positional = raw.match(/^slide-(\d+)$/);
    if (positional) {
      const num = parseInt(positional[1], 10);
      if (num >= 1 && num <= this.totalSlides) return num - 1;
      return 0;
    }

    // Semantic form: #revenue, #forecast, … If the current deck doesn't
    // render a slide with that slug (e.g. comparing a report type that
    // omits the section) fall back to the first slide.
    if (this.indexBySlug && this.indexBySlug.has(raw)) {
      return this.indexBySlug.get(raw);
    }
    return 0;
  }

  // --- navigation -----------------------------------------------------

  next() {
    const after = this.visibleIndices().find((i) => i > this.indexValue);
    if (after !== undefined) this.showSlide(after);
  }

  previous() {
    const before = this.visibleIndices().filter((i) => i < this.indexValue).pop();
    if (before !== undefined) this.showSlide(before);
  }

  goToSlide(index) {
    this.showSlide(index);
  }

  // A chapter button: the chapter's first board.
  jump(event) {
    const chapter = event.currentTarget.dataset.chapter;
    const first = this.visibleIndices().find((i) => this.slideTargets[i].dataset.chapter === chapter);
    if (first !== undefined) {
      if (this.overview) this.toggleOverview();
      this.showSlide(first);
    }
  }

  // --- hiding, notes, presenter, share ---------------------------------

  // Hide or show a board for this presentation. Saved on the report, so
  // the boss's share link and the PDF hide it too.
  async toggleHidden(event) {
    event.stopPropagation();
    const button = event.currentTarget;
    const board = button.closest('[data-presentation-target="slide"]');
    const slug = button.dataset.slug;
    const hiding = board.dataset.hidden !== "true";
    const hidden = new Set(this.slideTargets.filter((s) => s.dataset.hidden === "true").map((s) => s.dataset.slideSlug));
    if (hiding) hidden.add(slug);
    else hidden.delete(slug);

    const url = this.element.dataset.presentationSettingsUrl;
    if (!url) return;
    const token = document.querySelector('meta[name="csrf-token"]')?.content;
    const response = await fetch(url, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": token },
      body: JSON.stringify({ hidden_boards: [...hidden] }),
    });
    if (!response.ok) return;

    this.slideTargets.forEach((s) => {
      if (s.dataset.slideSlug !== slug) return;
      if (hiding) s.dataset.hidden = "true";
      else delete s.dataset.hidden;
    });
    this.hideTargets.filter((b) => b.dataset.slug === slug).forEach((b) => {
      b.textContent = hiding ? b.dataset.showLabel : b.dataset.hideLabel;
      b.setAttribute("aria-pressed", String(hiding));
    });
    this.totalVisible = this.visibleIndices().length;
    this.showSlide(this.indexValue, false, false);
  }

  toggleNotes() {
    this.notesOpen = !this.notesOpen;
    this.renderNotes();
  }

  // The presenter's notes for the board on show: its headline, Saga's read
  // and its list, then the board after it.
  renderNotes() {
    if (!this.hasNotesPanelTarget) return;
    this.notesPanelTarget.hidden = !this.notesOpen;
    if (this.hasNotesButtonTarget) this.notesButtonTarget.setAttribute("aria-pressed", String(this.notesOpen));
    if (!this.notesOpen) return;

    const board = this.slideTargets[this.indexValue];
    const text = (selector) => Array.from(board?.querySelectorAll(selector) || []).map((n) => n.textContent.trim()).filter(Boolean);
    const headline = text(".deck-headline, .deck-cover-title, .deck-statement")[0] || "";
    const read = text(".deck-standfirst, .deck-lede");
    // A board that is one figure has no headline: say the figure.
    const figures = text(".deck-figure-hero, .deck-figure-xl, .deck-figure-l, .deck-figure-m").slice(0, 4);
    if (figures.length) read.unshift(figures.join(" · "));
    const items = text(".deck-list .deck-prose, .deck-list > div > div > div:first-child").slice(0, 6);
    const parts = [headline ? `<strong>${headline}</strong>` : "", ...read.map((r) => `<p>${r}</p>`), items.length ? `<ul>${items.map((i) => `<li>${i}</li>`).join("")}</ul>` : ""];
    this.notesTarget.innerHTML = parts.filter(Boolean).join("");

    const after = this.visibleIndices().find((i) => i > this.indexValue);
    const nextBoard = after !== undefined ? this.slideTargets[after] : null;
    const nextTitle = nextBoard ? (nextBoard.querySelector(".deck-headline, .deck-cover-title, .deck-statement")?.textContent.trim() || nextBoard.dataset.slideSlug) : "";
    if (this.hasNextUpTarget) this.nextUpTarget.textContent = nextTitle ? `${this.element.dataset.presentationNextLabel}: ${nextTitle}` : "";
  }

  startClock() {
    const tick = () => {
      if (this.hasClockTarget) this.clockTarget.textContent = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    };
    tick();
    this.clockTimer = setInterval(tick, 15000);
  }

  // A second window on the laptop while the room has the first: notes and
  // the clock always open, kept on the same board through the channel.
  openPresenter(event) {
    event.preventDefault();
    const url = new URL(window.location.href);
    url.searchParams.set("presenter", "1");
    url.hash = this.slideRef(this.indexValue);
    window.open(url.toString(), "carttype-presenter", "width=1100,height=700");
  }

  // The theme, switched without leaving the deck. Two themes draw a board
  // with different markup (duotone's label column, blocks' panels), so the
  // switch re-renders rather than restyles: the owner's choice is saved
  // first, then the deck reloads on the same board with the theme in the
  // URL — which is also how anyone else previews one.
  async switchTheme(event) {
    const key = event.currentTarget.dataset.theme;
    this.themeTargets.forEach((button) => {
      const current = button.dataset.theme === key;
      button.classList.toggle("is-current", current);
      button.setAttribute("aria-pressed", String(current));
    });

    const save = this.element.dataset.presentationThemeUrl;
    if (save) {
      const token = document.querySelector('meta[name="csrf-token"]')?.content;
      await fetch(save, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": token },
        body: JSON.stringify({ account: { deck_theme: key } }),
      });
    }

    const url = new URL(window.location.href);
    url.searchParams.set("theme", key);
    url.hash = this.slideRef(this.indexValue);
    window.location.replace(url.toString());
  }

  async copyShare(event) {
    if (!this.hasShareUrlTarget) return;
    const button = event.currentTarget;
    try {
      await navigator.clipboard.writeText(this.shareUrlTarget.value);
      const label = button.textContent;
      button.textContent = button.dataset.copiedLabel;
      setTimeout(() => (button.textContent = label), 1500);
    } catch {
      this.shareUrlTarget.select();
    }
  }

  // In the overview, a board is a button to itself.
  handlePick(event) {
    if (!this.overview) return;
    const board = event.target.closest('[data-presentation-target="slide"]');
    if (!board) return;
    const index = this.slideTargets.indexOf(board);
    if (index < 0) return;
    this.toggleOverview();
    this.showSlide(index);
  }

  toggleOverview() {
    if (!this.hasStageTarget || this.stacked) return;
    this.overview = !this.overview;
    this.stageTarget.classList.toggle("deck-stage--overview", this.overview);
    if (this.hasOverviewButtonTarget) {
      this.overviewButtonTarget.setAttribute(
        "aria-pressed",
        String(this.overview),
      );
    }
    if (this.overview) {
      this.hydrateAll();
      this.slideTargets[this.indexValue]?.scrollIntoView({ block: "center" });
    }
  }

  // The deck's own frame fills the screen; the nav goes with the chrome.
  toggleFullscreen() {
    if (document.fullscreenElement) {
      document.exitFullscreen?.();
    } else {
      this.element.requestFullscreen?.();
    }
  }

  openCompare(event) {
    event.preventDefault();
    const link = event.currentTarget;
    // Prefer the semantic slug so the sibling deck opens on the same
    // section even if its slide numbering differs. Fall back to the
    // positional form for slides that haven't been slugged yet.
    window.open(`${link.href}#${this.slideRef(this.indexValue)}`, "_blank");
  }

  // Leaving for a page that sends the merchant back to this deck (the theme
  // picker): take the current slide along so the deck reopens on it.
  carrySlide(event) {
    event.preventDefault();
    const url = new URL(event.currentTarget.href);
    url.searchParams.set("slide", this.slideRef(this.indexValue));
    Turbo.visit(url.toString());
  }

  // The slide's semantic slug, or its position for slides without one.
  slideRef(index) {
    const slug = this.slugByIndex && this.slugByIndex[index];
    return slug || `slide-${index + 1}`;
  }

  exit() {
    const exitUrl = this.element.dataset.presentationExitUrl;
    if (exitUrl) {
      Turbo.visit(exitUrl);
    } else {
      history.back();
    }
  }

  // --- photos ---------------------------------------------------------

  // Product photos arrive as `data-src` and are fetched for the board on
  // show and its neighbours, so opening a deck does not fetch every photo
  // in it (DeckHelper#deck_photo).
  hydrate(index) {
    for (let i = index - 1; i <= index + 1; i++) {
      this.slideTargets[i]?.querySelectorAll("img[data-src]").forEach((img) => {
        img.src = img.dataset.src;
        delete img.dataset.src;
      });
    }
  }

  hydrateAll() {
    this.element.querySelectorAll("img[data-src]").forEach((img) => {
      img.src = img.dataset.src;
      delete img.dataset.src;
    });
  }

  // --- show -----------------------------------------------------------

  // The board on show is marked `.is-current`, and that class is what the
  // deck's motion hangs off (deck.css, "Motion"): a short fade on the
  // board, its bars rising and lines drawing once. Stacked, every board
  // stays on the page and the current one scrolls into view.
  showSlide(index, updateHash = true, broadcast = true) {
    this.indexValue = index;
    this.hydrate(index);
    if (broadcast) this.channel?.postMessage({ index });

    this.slideTargets.forEach((slide, i) => {
      const current = i === index;
      if (this.stacked) {
        slide.style.visibility = "";
        slide.style.zIndex = "";
        slide.removeAttribute("aria-hidden");
      } else {
        slide.style.visibility = current ? "visible" : "hidden";
        slide.style.zIndex = current ? "1" : "0";
        slide.setAttribute("aria-hidden", current ? "false" : "true");
      }
      slide.classList.toggle("is-current", current);
    });

    if (this.stacked && updateHash) {
      this.slideTargets[index]?.scrollIntoView({
        block: "start",
        behavior: "smooth",
      });
    }

    // Update URL hash without triggering hashchange loop — prefer the
    // semantic slug so deep-links stay stable across report types.
    if (updateHash) {
      history.replaceState(null, "", `#${this.slideRef(index)}`);
    }

    // The count and the progress are over the boards the room will see.
    const visible = this.visibleIndices();
    const position = Math.max(visible.indexOf(index), 0);
    if (this.hasCounterTarget) {
      const pad = (n) => String(n).padStart(2, "0");
      this.counterTarget.textContent = `${pad(position + 1)} / ${pad(visible.length)}`;
    }

    if (this.hasProgressTarget) {
      const share =
        visible.length > 1 ? (position / (visible.length - 1)) * 100 : 100;
      this.progressTarget.style.setProperty(
        "--deck-progress",
        `${share.toFixed(2)}%`,
      );
    }

    this.renderNotes();

    const chapter = this.slideTargets[index]?.dataset.chapter;
    this.chapterTargets.forEach((button) => {
      const current = button.dataset.chapter === chapter;
      button.classList.toggle("is-current", current);
      if (current) button.setAttribute("aria-current", "true");
      else button.removeAttribute("aria-current");
    });

    if (this.hasPrevButtonTarget) {
      this.prevButtonTarget.disabled = position === 0;
    }
    if (this.hasNextButtonTarget) {
      this.nextButtonTarget.disabled = position === visible.length - 1;
    }
  }
}
